import {useEffect, useSyncExternalStore} from "react";
import store from "@/store";
import {ScoutingVisionsAPI as api} from "@/api/scouting_visions";
import {
	getDsContext,
	isDsContextReady,
	onDsContextChange,
} from "@/utils/dsContext";
import {isEmptyRow, updateRow} from "@/utils/scoutingVisions";

const initial = () => ({
	data: {},
	matches: {},
	matchStatus: {},
	matchErrors: {},
	jobs: {},
	status: "idle",
	error: null,
});
let state = initial();
let generation = 0;
let bulkRequest = null;
const matchRequests = new Map();
const jobs = new Map();
const listeners = new Set();
const emptyVision = {mode: "live", rows: {}};
const publish = (changes) => {
	state = {...state, ...changes};
	listeners.forEach((listener) => listener());
};
const normalize = (vision) => ({
	...vision,
	rows: Object.fromEntries(
		(vision.rows || []).map((row) => [row.playerId, row]),
	),
});
const byMatch = (visions) => {
	const result = {};
	visions.forEach((v) => {
		(result[v.matchId] ||= {})[v.observer.id] = normalize(v);
	});
	return result;
};
onDsContextChange(() => {
	generation++;
	bulkRequest = null;
	matchRequests.clear();
	jobs.forEach((job) => clearTimeout(job.timer));
	jobs.clear();
	publish(initial());
});
const canContribute = () => {
	const user = store.getState().user;
	return (
		user?.roleName?.toLowerCase() === "admin" ||
		user?.areaPermissions?.some((p) => p.area === "Scouting")
	);
};
export const visionData = () => {
	getDsContext();
	return state;
};
export const refreshVisions = (force = false) => {
	const context = getDsContext();
	if (!isDsContextReady())
		return Promise.reject(new Error("Sessione scouting non disponibile"));
	if (bulkRequest && !force) return bulkRequest;
	const ticket = ++generation;
	publish({status: "loading", error: null});
	const request = api
		.list(store.getState().season.id)
		.then((rows) => {
			if (context !== getDsContext() || ticket !== generation)
				throw new Error("Richiesta superata");
			publish({data: byMatch(rows), status: "ready", error: null});
		})
		.catch((error) => {
			if (context === getDsContext() && ticket === generation)
				publish({data: {}, status: "error", error});
			throw error;
		})
		.finally(() => {
			if (bulkRequest === request) bulkRequest = null;
		});
	bulkRequest = request;
	return request;
};
export const refreshMatchVisions = (matchId) => {
	const context = getDsContext();
	if (!isDsContextReady())
		return Promise.reject(new Error("Sessione scouting non disponibile"));
	if (matchRequests.has(matchId)) return matchRequests.get(matchId);
	if (jobs.get(matchId)?.pending) return Promise.resolve();
	publish({
		matchStatus: {
			...state.matchStatus,
			[matchId]: state.matchStatus[matchId] === "ready" ? "ready" : "loading",
		},
		matchErrors: {...state.matchErrors, [matchId]: null},
	});
	const request = api
		.match(matchId)
		.then((rows) => {
			if (context !== getDsContext())
				throw new Error("Sessione scouting cambiata");
			if (!jobs.get(matchId)?.pending)
				publish({
					matches: {...state.matches, [matchId]: byMatch(rows)[matchId] || {}},
					matchStatus: {...state.matchStatus, [matchId]: "ready"},
				});
		})
		.catch((error) => {
			if (context === getDsContext())
				publish({
					matches: {...state.matches, [matchId]: {}},
					matchStatus: {...state.matchStatus, [matchId]: "error"},
					matchErrors: {...state.matchErrors, [matchId]: error},
				});
			throw error;
		})
		.finally(() => {
			if (matchRequests.get(matchId) === request) matchRequests.delete(matchId);
		});
	matchRequests.set(matchId, request);
	return request;
};
const publishJob = (id, job) =>
	publish({
		jobs: {
			...state.jobs,
			[id]: {
				pending: job.pending,
				saving: job.saving,
				error: job.error,
				draft: job.draft,
			},
		},
	});
export const flushVision = (matchId, keepalive = false) => {
	const context = getDsContext();
	const job = jobs.get(matchId);
	if (!job || !job.pending || job.context !== context) return Promise.resolve();
	clearTimeout(job.timer);
	if (job.saving)
		return job.promise.then(() =>
			job.pending ? flushVision(matchId, keepalive) : undefined,
		);
	const sequence = job.sequence;
	const draft = job.draft;
	const body = {
		mode: draft.mode,
		rows: Object.values(draft.rows || {})
			.filter((row) => !isEmptyRow(row))
			.map(({playerId, side, starter, attitudes, note}) => ({
				playerId,
				side,
				starter,
				attitudes: attitudes || [],
				note: note || null,
			})),
	};
	job.saving = true;
	job.error = null;
	publishJob(matchId, job);
	job.promise = api
		.put(matchId, body, job.token, keepalive)
		.then((result) => {
			if (context !== getDsContext()) return;
			const saved = normalize(result);
			publish({
				matches: {
					...state.matches,
					[matchId]: {...state.matches[matchId], [result.observer.id]: saved},
				},
			});
			if (sequence === job.sequence) {
				job.pending = false;
				job.draft = saved;
			}
			if (state.status !== "idle") refreshVisions(true).catch(() => {});
		})
		.catch((error) => {
			if (context === getDsContext()) {
				job.error = error;
				job.pending = true;
			}
			throw error;
		})
		.finally(() => {
			job.saving = false;
			if (context === getDsContext()) publishJob(matchId, job);
		});
	return job.promise.then(() => {
		if (context === getDsContext() && job.pending && !job.error)
			return flushVision(matchId, keepalive);
	});
};
export const changeVision = (matchId, patch) => {
	const context = getDsContext();
	if (
		!isDsContextReady() ||
		!canContribute() ||
		state.matchStatus[matchId] !== "ready"
	)
		return;
	const userId = store.getState().user.userId;
	let job = jobs.get(matchId);
	if (!job) {
		job = {
			context,
			token: store.getState().token,
			draft: state.matches[matchId]?.[userId] || emptyVision,
			sequence: 0,
		};
		jobs.set(matchId, job);
	}
	if (!job.pending) job.draft = state.matches[matchId]?.[userId] || emptyVision;
	job.draft = {...job.draft, ...patch};
	job.sequence++;
	job.pending = true;
	job.error = null;
	clearTimeout(job.timer);
	job.timer = setTimeout(() => flushVision(matchId).catch(() => {}), 1000);
	publishJob(matchId, job);
};
const hasPending = () => [...jobs.values()].some((job) => job.pending);
if (typeof window !== "undefined") {
	window.addEventListener("beforeunload", (event) => {
		if (hasPending()) {
			event.preventDefault();
			event.returnValue = "";
		}
	});
	window.addEventListener("pagehide", () => {
		jobs.forEach((job, id) => {
			if (job.pending) flushVision(id, true).catch(() => {});
		});
	});
}
export const useScoutingVisions = ({matchId, enabled = true} = {}) => {
	const context = getDsContext();
	const current = useSyncExternalStore(
		(listener) => {
			listeners.add(listener);
			return () => listeners.delete(listener);
		},
		() => state,
	);
	useEffect(() => {
		if (!enabled || !isDsContextReady()) return;
		const reload = () => {
			if (!document.hidden)
				(matchId ? refreshMatchVisions(matchId) : refreshVisions()).catch(
					() => {},
				);
		};
		reload();
		window.addEventListener("focus", reload);
		const timer = setInterval(reload, 30000);
		return () => {
			window.removeEventListener("focus", reload);
			clearInterval(timer);
			if (matchId) flushVision(matchId).catch(() => {});
		};
	}, [context, matchId, enabled]);
	const observerId = store.getState().user?.userId;
	const job = current.jobs[matchId];
	const vision = job?.pending
		? job.draft
		: current.matches[matchId]?.[observerId] || emptyVision;
	return {
		...current,
		vision,
		visions: current.data,
		matchVisions: Object.values(current.matches[matchId] || {}),
		pending: !!job?.pending,
		saving: !!job?.saving,
		saveError: job?.error,
		loading: matchId
			? !current.matchStatus[matchId] ||
				current.matchStatus[matchId] === "loading"
			: current.status === "idle" || current.status === "loading",
		readError: matchId ? current.matchErrors[matchId] : current.error,
		canWrite:
			!!enabled &&
			!!canContribute() &&
			current.matchStatus[matchId] === "ready",
		refresh: () => (matchId ? refreshMatchVisions(matchId) : refreshVisions()),
		flush: () => flushVision(matchId),
		setMode: (mode) => changeVision(matchId, {mode}),
		setRow: (playerId, side, patch) =>
			changeVision(matchId, {
				rows: updateRow(
					(jobs.get(matchId)?.pending
						? jobs.get(matchId).draft
						: state.matches[matchId]?.[observerId] || emptyVision
					).rows,
					playerId,
					side,
					patch,
				),
			}),
	};
};
